import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { User, Phone, Mail, MapPin, Calendar, AlertCircle, Edit } from "lucide-react";
import { useState } from "react";
import { MedicalInfoEditor } from "./MedicalInfoEditor";

// Mock patient data
const mockPatientData = {
  personalInfo: {
    firstName: "John",
    lastName: "Doe",
    dateOfBirth: "1985-06-15",
    gender: "Male",
    phone: "(555) 123-4567",
    email: "john.doe@email.com",
    address: "123 Main St, Anytown, ST 12345"
  },
  emergencyContact: {
    name: "Jane Doe",
    relationship: "Spouse",
    phone: "(555) 987-6543"
  },
  medicalInfo: {
    allergies: ["Penicillin", "Shellfish"],
    medications: ["Lisinopril 10mg", "Metformin 500mg"],
    conditions: ["Hypertension", "Type 2 Diabetes"],
    bloodType: "A+",
    insurance: {
      provider: "Blue Cross Blue Shield",
      policyNumber: "BC123456789",
      groupNumber: "GRP001"
    }
  }
};

export function PatientProfile() {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(mockPatientData);

  const handleSave = () => {
    // In a real app, this would save to your backend
    setIsEditing(false);
    console.log("Saving profile data:", formData);
  };

  const handleMedicalInfoUpdate = (updatedMedicalInfo: any) => {
    setFormData({
      ...formData,
      medicalInfo: updatedMedicalInfo
    });
  };

  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>Your basic contact and demographic information</CardDescription>
          </div>
          <Button
            variant={isEditing ? "default" : "outline"}
            onClick={() => isEditing ? handleSave() : setIsEditing(true)}
          >
            <Edit className="w-4 h-4 mr-2" />
            {isEditing ? "Save Changes" : "Edit Profile"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={formData.personalInfo.firstName}
                onChange={(e) => setFormData({
                  ...formData,
                  personalInfo: { ...formData.personalInfo, firstName: e.target.value }
                })}
                disabled={!isEditing}
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={formData.personalInfo.lastName}
                onChange={(e) => setFormData({
                  ...formData,
                  personalInfo: { ...formData.personalInfo, lastName: e.target.value }
                })}
                disabled={!isEditing}
              />
            </div>
            <div>
              <Label htmlFor="dateOfBirth">Date of Birth</Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={formData.personalInfo.dateOfBirth}
                onChange={(e) => setFormData({
                  ...formData,
                  personalInfo: { ...formData.personalInfo, dateOfBirth: e.target.value }
                })}
                disabled={!isEditing}
              />
            </div>
            <div>
              <Label htmlFor="gender">Gender</Label>
              <Input
                id="gender"
                value={formData.personalInfo.gender}
                disabled={!isEditing}
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={formData.personalInfo.phone}
                onChange={(e) => setFormData({
                  ...formData,
                  personalInfo: { ...formData.personalInfo, phone: e.target.value }
                })}
                disabled={!isEditing}
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.personalInfo.email}
                onChange={(e) => setFormData({
                  ...formData,
                  personalInfo: { ...formData.personalInfo, email: e.target.value }
                })}
                disabled={!isEditing}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="address">Address</Label>
            <Textarea
              id="address"
              value={formData.personalInfo.address}
              onChange={(e) => setFormData({
                ...formData,
                personalInfo: { ...formData.personalInfo, address: e.target.value }
              })}
              disabled={!isEditing}
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader>
          <CardTitle>Emergency Contact</CardTitle>
          <CardDescription>Person to contact in case of emergency</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="emergencyName">Name</Label>
              <Input
                id="emergencyName"
                value={formData.emergencyContact.name}
                disabled={!isEditing}
              />
            </div>
            <div>
              <Label htmlFor="relationship">Relationship</Label>
              <Input
                id="relationship"
                value={formData.emergencyContact.relationship}
                disabled={!isEditing}
              />
            </div>
            <div>
              <Label htmlFor="emergencyPhone">Phone Number</Label>
              <Input
                id="emergencyPhone"
                value={formData.emergencyContact.phone}
                disabled={!isEditing}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Medical Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Medical Information
              </CardTitle>
              <CardDescription>Important medical details for your care</CardDescription>
            </div>
            <MedicalInfoEditor 
              medicalInfo={formData.medicalInfo}
              onUpdate={handleMedicalInfoUpdate}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-base font-medium">Blood Type</Label>
            <p className="text-2xl font-bold text-red-600 mt-1">{formData.medicalInfo.bloodType}</p>
          </div>

          <Separator />

          <div>
            <Label className="text-base font-medium">Allergies</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.medicalInfo.allergies.map((allergy, index) => (
                <Badge key={index} variant="destructive">{allergy}</Badge>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-base font-medium">Current Medications</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.medicalInfo.medications.map((medication, index) => (
                <Badge key={index} variant="secondary">{medication}</Badge>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-base font-medium">Medical Conditions</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.medicalInfo.conditions.map((condition, index) => (
                <Badge key={index} variant="outline">{condition}</Badge>
              ))}
            </div>
          </div>

          <Separator />

          <div>
            <Label className="text-base font-medium">Insurance Information</Label>
            <div className="grid gap-4 md:grid-cols-3 mt-2">
              <div>
                <Label className="text-sm text-muted-foreground">Provider</Label>
                <p className="font-medium">{formData.medicalInfo.insurance.provider}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Policy Number</Label>
                <p className="font-medium">{formData.medicalInfo.insurance.policyNumber}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Group Number</Label>
                <p className="font-medium">{formData.medicalInfo.insurance.groupNumber}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
